import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShieldAlert, Plus, Trash2, EyeOff, Trash } from 'lucide-react';
import type { NegativeWord, NegativeFilterAction } from '@/services/api/comment-auto-replies';

interface Props {
    enabled: boolean;
    action: NegativeFilterAction;
    words: NegativeWord[];
    onChange: (enabled: boolean, action: NegativeFilterAction, words: NegativeWord[]) => void;
    disabled?: boolean;
}

export default function NegativeWordsConfig({ enabled, action, words, onChange, disabled }: Props) {
    const [newWord, setNewWord] = useState('');
    const [newType, setNewType] = useState<'exact' | 'contains'>('contains');

    const addWord = () => {
        const w = newWord.trim();
        if (!w) return;
        // Evita duplicata (mesma word+match_type)
        const exists = words.some(x => x.word.toLowerCase() === w.toLowerCase() && x.match_type === newType);
        if (exists) {
            setNewWord('');
            return;
        }
        onChange(enabled, action, [...words, { word: w, match_type: newType }]);
        setNewWord('');
    };

    const removeWord = (idx: number) => {
        onChange(enabled, action, words.filter((_, i) => i !== idx));
    };

    return (
        <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-3 space-y-3">
            {/* Toggle principal */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-orange-600" />
                    <div>
                        <p className="text-sm font-medium text-gray-700">Filtro de palavras negativas</p>
                        <p className="text-xs text-gray-500">
                            Esconde ou deleta comentários com palavras ofensivas. O flow continua normal depois.
                        </p>
                    </div>
                </div>
                <Switch
                    checked={enabled}
                    onCheckedChange={v => onChange(v, action, words)}
                    disabled={disabled}
                />
            </div>

            {enabled && (
                <div className="space-y-3 pt-2 border-t border-orange-200/60">
                    {/* Ação ao detectar */}
                    <div>
                        <p className="text-xs uppercase font-semibold text-gray-500 mb-1.5">Ao detectar palavra negativa</p>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => onChange(enabled, 'hide', words)}
                                className={`p-2.5 rounded-lg border-2 transition-colors text-left flex items-center gap-2 ${action === 'hide'
                                    ? 'border-orange-500 bg-orange-100'
                                    : 'border-gray-200 bg-white hover:border-gray-300'}`}
                            >
                                <EyeOff className={`w-4 h-4 ${action === 'hide' ? 'text-orange-600' : 'text-gray-400'}`} />
                                <div>
                                    <p className="text-xs font-semibold">Esconder</p>
                                    <p className="text-[10px] text-gray-500">Some do feed público</p>
                                </div>
                            </button>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => onChange(enabled, 'delete', words)}
                                className={`p-2.5 rounded-lg border-2 transition-colors text-left flex items-center gap-2 ${action === 'delete'
                                    ? 'border-red-500 bg-red-100'
                                    : 'border-gray-200 bg-white hover:border-gray-300'}`}
                            >
                                <Trash className={`w-4 h-4 ${action === 'delete' ? 'text-red-600' : 'text-gray-400'}`} />
                                <div>
                                    <p className="text-xs font-semibold">Excluir</p>
                                    <p className="text-[10px] text-gray-500">Remove permanentemente</p>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Lista de palavras configuradas */}
                    <div>
                        <p className="text-xs uppercase font-semibold text-gray-500 mb-1.5">
                            Palavras / frases ({words.length})
                        </p>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                            {words.length === 0 && (
                                <p className="text-xs text-gray-400 italic text-center py-2">
                                    Nenhuma palavra adicionada ainda
                                </p>
                            )}
                            {words.map((w, i) => (
                                <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-white rounded border border-gray-200">
                                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${w.match_type === 'exact'
                                        ? 'bg-violet-100 text-violet-700'
                                        : 'bg-blue-100 text-blue-700'}`}>
                                        {w.match_type === 'exact' ? 'EXATA' : 'CONTÉM'}
                                    </span>
                                    <span className="text-sm text-gray-700 flex-1 truncate">{w.word}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeWord(i)}
                                        disabled={disabled}
                                        className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-600"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Adicionar nova palavra */}
                    <div className="space-y-1.5">
                        <p className="text-xs uppercase font-semibold text-gray-500">Adicionar palavra ou frase</p>
                        <div className="flex gap-2">
                            <Select
                                value={newType}
                                onValueChange={(v: 'exact' | 'contains') => setNewType(v)}
                                disabled={disabled}
                            >
                                <SelectTrigger className="w-28 h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="contains">Contém</SelectItem>
                                    <SelectItem value="exact">Exata</SelectItem>
                                </SelectContent>
                            </Select>
                            <Input
                                value={newWord}
                                onChange={e => setNewWord(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addWord();
                                    }
                                }}
                                placeholder={newType === 'exact' ? 'Ex: lixo' : 'Ex: bosta de empresa'}
                                disabled={disabled}
                                className="h-9 flex-1"
                                maxLength={255}
                            />
                            <Button
                                type="button"
                                size="sm"
                                onClick={addWord}
                                disabled={disabled || !newWord.trim()}
                                className="h-9"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                        <p className="text-[11px] text-gray-500 leading-relaxed">
                            <strong>Contém:</strong> bate quando aparece dentro do comentário (ex.: "lixo" → "isso é lixo").<br />
                            <strong>Exata:</strong> bate só na palavra inteira (não dentro de outra). Match é case-insensitive.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
